import React, { useEffect, useRef, useState } from "react";
import { Send, ShieldAlert, AlertTriangle } from "lucide-react";
import { sendChat, getChatHistory } from "../lib/api";

const SESSION_KEY = "mw_companion_session";

const Companion = () => {
    const [sessionId, setSessionId] = useState(null);
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const bottomRef = useRef(null);

    useEffect(() => {
        let sid = localStorage.getItem(SESSION_KEY);
        if (!sid) {
            sid = `mw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
            localStorage.setItem(SESSION_KEY, sid);
        }
        setSessionId(sid);
        getChatHistory(sid).then(setMessages).catch(() => setMessages([]));
    }, []);

    useEffect(() => {
        bottomRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    const send = async (e) => {
        e?.preventDefault();
        if (!input.trim() || loading || !sessionId) return;

        const userText = input.trim();
        const ts = new Date().toISOString();
        setMessages((m) => [...m, { role: "user", content: userText, timestamp: ts }]);
        setInput("");
        setLoading(true);

        try {
            const res = await sendChat(sessionId, userText);
            setMessages((m) => [
                ...m,
                { role: "assistant", content: res.response, timestamp: res.timestamp },
            ]);
        } catch (err) {
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        "The line dropped. Try again. If you are in immediate crisis, call or text 988 right now — that is a real human, available 24/7.",
                    timestamp: new Date().toISOString(),
                    error: true,
                },
            ]);
        } finally {
            setLoading(false);
        }
    };

    const startNew = () => {
        const sid = `mw-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
        localStorage.setItem(SESSION_KEY, sid);
        setSessionId(sid);
        setMessages([]);
    };

    return (
        <div className="bg-black min-h-screen" data-testid="companion-page">
            <section className="border-b border-white/10 py-12 md:py-16">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="flex items-center gap-3 mb-4 text-xs tracking-[0.4em] text-red-600 font-display uppercase">
                        <span className="h-px w-12 bg-red-600" />
                        Battlefield Companion
                    </div>
                    <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tighter leading-[0.9]">
                        Talk it <span className="text-red-600">out.</span>
                    </h1>
                    <p className="mt-4 text-white/65 max-w-2xl">
                        A compassionate, scripture-aware companion built to fight alongside you —
                        rooted in neuroscience and the truth of Christ. Not a therapist. Not a
                        replacement for help. A friend in the trench.
                    </p>
                </div>
            </section>

            {/* Safety notice */}
            <div className="bg-[#0a0a0a] border-b border-white/10 py-4">
                <div className="max-w-4xl mx-auto px-6 lg:px-10 flex items-start gap-3 text-xs text-white/70">
                    <ShieldAlert size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <p>
                        If you're in crisis, please stop and call or text <a href="tel:988" className="text-red-600 underline">988</a> now.
                        This companion is not a substitute for emergency or professional care.
                    </p>
                </div>
            </div>

            {/* Chat area */}
            <section className="py-10">
                <div className="max-w-4xl mx-auto px-6 lg:px-10">
                    <div className="border border-white/10 bg-[#050505] min-h-[50vh] flex flex-col">
                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-6 md:p-8 space-y-6" data-testid="chat-messages">
                            {messages.length === 0 && (
                                <div className="text-white/50 text-center py-12">
                                    <div className="font-stencil tracking-widest text-xs text-red-600 mb-3">[ CONNECTION OPEN ]</div>
                                    <p>Say what you're carrying. No judgment. No script.</p>
                                </div>
                            )}
                            {messages.map((m, i) => (
                                <div
                                    key={i}
                                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                                    data-testid={`message-${m.role}`}
                                >
                                    <div
                                        className={`max-w-[85%] p-4 leading-relaxed text-[15px] whitespace-pre-line ${
                                            m.role === "user"
                                                ? "bg-red-700 text-white"
                                                : m.error
                                                ? "bg-black border border-red-700 text-white/90"
                                                : "bg-black border border-white/15 text-white/90 glow-red"
                                        }`}
                                    >
                                        {m.role === "assistant" && (
                                            <div className="font-stencil text-xs tracking-widest text-red-600 mb-2">
                                                COMPANION
                                            </div>
                                        )}
                                        {m.content}
                                    </div>
                                </div>
                            ))}
                            {loading && (
                                <div className="flex justify-start">
                                    <div className="bg-black border border-white/15 p-4 glow-red">
                                        <div className="font-stencil text-xs tracking-widest text-red-600 mb-2">COMPANION</div>
                                        <div className="flex gap-1.5">
                                            <span className="w-2 h-2 bg-red-600 animate-pulse" />
                                            <span className="w-2 h-2 bg-red-600 animate-pulse" style={{ animationDelay: "0.15s" }} />
                                            <span className="w-2 h-2 bg-red-600 animate-pulse" style={{ animationDelay: "0.3s" }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            <div ref={bottomRef} />
                        </div>

                        {/* Input */}
                        <form
                            onSubmit={send}
                            className="border-t border-white/10 flex items-stretch"
                            data-testid="chat-form"
                        >
                            <textarea
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        send();
                                    }
                                }}
                                placeholder="What's the war today?"
                                rows={2}
                                className="flex-1 p-4 bg-black text-white placeholder-white/30 resize-none outline-none focus:border-red-600 text-[15px]"
                                data-testid="chat-input"
                            />
                            <button
                                type="submit"
                                disabled={loading || !input.trim()}
                                className="px-6 bg-red-700 hover:bg-red-800 disabled:opacity-30 disabled:cursor-not-allowed text-white font-display tracking-[0.15em] uppercase transition-colors flex items-center gap-2"
                                data-testid="chat-send"
                            >
                                <Send size={16} />
                                <span className="hidden sm:inline">Send</span>
                            </button>
                        </form>
                    </div>

                    <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-xs">
                        <div className="flex items-center gap-2 text-white/40">
                            <AlertTriangle size={12} />
                            Conversations are saved on this device only.
                        </div>
                        <button
                            onClick={startNew}
                            className="text-white/60 hover:text-red-600 font-display tracking-[0.2em] uppercase transition-colors"
                            data-testid="new-conversation-btn"
                        >
                            Start New Conversation
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Companion;
